import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BenefitDetailsResponse } from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import { getAdminLegalDocuments } from 'core/application/adminDashboard/legalDocuments/benefitPlanDocuments'
import getBenefitDetailsForActivePlanPeriod from 'core/application/benefit/getBenefitDetailsForActivePlanPeriod'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'
import { LegalDocument } from 'core/domain/legalDocuments/legalDocuments'

import { BenefitPlanPeriodDropDown, Card, Row, Skeleton } from 'common/components'
import { useQueryParams, useQueryRequestV2 } from 'common/hooks'

import DocumentCard from './DocumentCard'
import Empty from './Empty'

import './DocumentCard.less'

const Summary = () => {
  const {
    data: benefitInfo,
    isLoading: isBenefitInfoLoading,
    sendQuery: sendGetBenefitInfo,
  } = useQueryRequestV2<BenefitDetailsResponse>({
    initialState: null,
  })
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([])
  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })
  const { isLoading: isLegalDocumentLoading, sendQuery: sendGetLegalDocuments } = useQueryRequestV2({
    initialState: null,
  })
  const { t: translate } = useTranslation()
  const queryParams = useQueryParams()
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()

  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    ;(async () => {
      if (selectedPlanPeriod) {
        await sendGetBenefitInfo(() => getBenefitDetailsForActivePlanPeriod())
        const data = await sendGetLegalDocuments(() => getAdminLegalDocuments(selectedPlanPeriod.id))

        let listOfLegalDocuments: LegalDocument[] = []
        data.forEach(
          (
            { documentType, planPeriodId, productClassId, title, description, fileUrl, apiUrl }: LegalDocument,
            index: number,
          ) => {
            let newLegalDocumentData: LegalDocument = {
              key: index + 1,
              documentType,
              productClassId,
              planPeriodId,
              title,
              description,
              fileUrl,
              apiUrl,
            }
            listOfLegalDocuments.push(newLegalDocumentData)
          },
        )
        setLegalDocuments(listOfLegalDocuments)
      }
    })()
  }, [selectedPlanPeriod])

  return (
    <Skeleton active loading={isLegalDocumentLoading || isBenefitInfoLoading || !benefitInfo} paragraph={{ rows: 10 }}>
      {selectedPlanPeriod && (
        <Card
          title={
            window.innerWidth > 474 && (
              <h5 key="title" className="legal-documents-text-decoration">
                {translate('admin-dashboard.legal-documents.title')}
                <br />
              </h5>
            )
          }
          className="legal_documents__summary"
          extra={
            <div key="summary" className="benefit_period">
              {planPeriods && (
                <BenefitPlanPeriodDropDown
                  defaultValue={planPeriodIdQueryParam}
                  planPeriods={planPeriods}
                  onPlanPeriodSelected={onPlanPeriodChanged}
                />
              )}
            </div>
          }
        >
          {benefitInfo &&
          legalDocuments &&
          (benefitInfo.benefitDesignStatus === BenefitDesignStatus.Signed ||
            benefitInfo.benefitDesignStatus === BenefitDesignStatus.Completed) ? (
            <Row>
              {legalDocuments.map(
                ({ documentType, planPeriodId, productClassId, title, description, fileUrl, apiUrl }) => (
                  <DocumentCard
                    documentType={documentType}
                    planPeriodId={planPeriodId}
                    productClassId={productClassId}
                    title={title}
                    link={fileUrl}
                    apiUrl={apiUrl}
                    linkName={translate('admin-dashboard.legal-documents.summary.view-document')}
                    description={description}
                  />
                ),
              )}
            </Row>
          ) : (
            <Empty planPeriod={selectedPlanPeriod.id} />
          )}
        </Card>
      )}
    </Skeleton>
  )
}

export default Summary
